import { Locale } from '@longy/common'
import { cn } from '@longy/web-components'
import { useId } from 'react'
import { match } from 'ts-pattern'

/**
 * Hand-drawn rather than emoji: Windows ships no glyphs for the regional-indicator flags, so
 * `🇭🇺` renders there as the letters "HU" in a box. Both flags are 1:2, which is why the default
 * size is an exact 24×12.
 *
 * Decorative by design — the trigger carries an `aria-label` and every menu item its own text, so a
 * `<title>` here would only make a screen reader say the same thing twice.
 */
type LocaleFlagProps = { locale: Locale; className?: string }

const FLAG_CLASS = 'h-3 w-6 shrink-0 rounded-[2px] ring-1 ring-black/10'

const HungarianFlag = ({ className }: { className?: string }) => (
    <svg aria-hidden className={className} focusable="false" viewBox="0 0 60 30">
        <rect width="60" height="10" fill="#CE2939" />
        <rect y="10" width="60" height="10" fill="#FFFFFF" />
        <rect y="20" width="60" height="10" fill="#477050" />
    </svg>
)

const UnionJackFlag = ({ className }: { className?: string }) => {
    // Scoped because the switcher renders this flag more than once on the same page, and a repeated
    // id would make every `url(#…)` resolve to whichever copy mounted first.
    const clipId = useId()

    return (
        <svg aria-hidden className={className} focusable="false" viewBox="0 0 60 30">
            <clipPath id={clipId}>
                <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
            </clipPath>
            <rect width="60" height="30" fill="#012169" />
            <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
            <path
                d="M0,0 L60,30 M60,0 L0,30"
                clipPath={`url(#${clipId})`}
                stroke="#C8102E"
                strokeWidth="4"
            />
            <path d="M30,0 v30 M0,15 h60" stroke="#FFFFFF" strokeWidth="10" />
            <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
        </svg>
    )
}

export const LocaleFlag = ({ locale, className }: LocaleFlagProps) =>
    match(locale)
        .with('hu', () => <HungarianFlag className={cn(FLAG_CLASS, className)} />)
        .with('en', () => <UnionJackFlag className={cn(FLAG_CLASS, className)} />)
        .exhaustive()
